import { useState, type FormEvent } from 'react';
import { ApiError } from '@lombok/api-client';
import { createBrandSchema } from '@lombok/shared';
import { Badge, Button, Card, cn } from '@lombok/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { QueryState } from '@/components/QueryState';
import { SortSelect } from '@/components/SortSelect';
import { TextField } from '@/components/form';
import { byDate, byNumber, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';
import {
  useBrands,
  useCategories,
  useCreateBrand,
  useCreateCategory,
  useDeleteBrand,
  useDeleteCategory,
  useUpdateBrand,
  useUpdateCategory,
} from '@/data/hooks';

interface Row {
  id: string;
  name: string;
  active: boolean;
  archived?: boolean;
  createdAt: string;
}

/** Active first, then inactive, then archived; by name inside each group. */
const statusRank = (r: Row) => (r.archived ? 2 : r.active ? 0 : 1);

const SORT_OPTIONS: SortOption<Row>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((r) => r.createdAt, 'desc') },
  { value: 'oldest', label: 'Más antiguas', compare: byDate((r) => r.createdAt) },
  { value: 'az', label: 'Nombre A–Z', compare: byText((r) => r.name) },
  { value: 'za', label: 'Nombre Z–A', compare: byText((r) => r.name, 'desc') },
  { value: 'status', label: 'Estado', compare: thenBy<Row>(byNumber(statusRank), byText((r) => r.name)) },
];

type TabKey = 'brands' | 'categories';

/** Wording that differs between the two tabs. */
const TABS: Record<TabKey, { label: string; noun: string; plural: string; newLabel: string }> = {
  brands: { label: 'Marcas', noun: 'marca', plural: 'marcas', newLabel: 'Nueva marca' },
  categories: { label: 'Categorías', noun: 'categoría', plural: 'categorías', newLabel: 'Nueva categoría' },
};

interface TabContentProps {
  tab: TabKey;
  rows: Row[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  create: (name: string) => Promise<unknown>;
  update: (id: string, input: { name?: string; active?: boolean; archived?: boolean }) => Promise<unknown>;
  remove: (id: string) => Promise<unknown>;
  showArchived: boolean;
  sort: string;
  creating: boolean;
  onCloseCreate: () => void;
}

/** Same rule as the API (shared schema); returns the Spanish message to show next to the field. */
function nameProblem(name: string): string | undefined {
  const parsed = createBrandSchema.shape.name.safeParse(name);
  return parsed.success ? undefined : 'El nombre debe tener entre 2 y 80 caracteres.';
}

const messageOf = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

function TabContent({
  tab,
  rows: allRows,
  isLoading,
  isError,
  error,
  onRetry,
  create,
  update,
  remove,
  showArchived,
  sort,
  creating,
  onCloseCreate,
}: TabContentProps) {
  const { noun, plural } = TABS[tab];
  // Finance reads the lists; creating and changing them is admin-only on the API.
  const isAdmin = useIsAdmin();
  const rows = sortItems(
    allRows.filter((r) => showArchived || !r.archived),
    SORT_OPTIONS,
    sort,
  );

  const [newName, setNewName] = useState('');
  const [newError, setNewError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string>();
  const [actingId, setActingId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'archive' | 'delete'; row: Row } | null>(null);

  const closeCreate = () => {
    setNewName('');
    setNewError(undefined);
    onCloseCreate();
  };

  const submitNew = async (e: FormEvent) => {
    e.preventDefault();
    const problem = nameProblem(newName);
    if (problem) return setNewError(problem);
    setSaving(true);
    setNewError(undefined);
    try {
      await create(newName);
      closeCreate();
    } catch (err) {
      setNewError(messageOf(err, `No se pudo crear la ${noun}.`));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: Row) => {
    setEditingId(row.id);
    setEditName(row.name);
    setEditError(undefined);
  };

  const saveEdit = async (id: string) => {
    const problem = nameProblem(editName);
    if (problem) return setEditError(problem);
    setActingId(id);
    setEditError(undefined);
    try {
      await update(id, { name: editName });
      setEditingId(null);
    } catch (err) {
      setEditError(messageOf(err, 'No se pudo guardar el cambio.'));
    } finally {
      setActingId(null);
    }
  };

  const toggle = async (row: Row) => {
    setActingId(row.id);
    try {
      await update(row.id, { active: !row.active });
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {creating ? (
        <Card className="p-4">
          <form onSubmit={(e) => void submitNew(e)} className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex-1">
              <TextField
                label={`Nombre de la nueva ${noun}`}
                name={`new-${noun}`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                error={newError}
                placeholder="Nombre"
                maxLength={80}
                autoFocus
              />
            </div>
            <div className="flex gap-2 sm:mt-7">
              <Button type="submit" size="sm" disabled={saving}>
                {saving ? 'Creando…' : 'Crear'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={closeCreate}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={onRetry}
          isEmpty={rows.length === 0}
          loadingVariant="table"
          skeletonCols={3}
          emptyTitle={`No hay ${plural}`}
          emptyDescription={isAdmin ? `Crea la primera con el botón “${TABS[tab].newLabel}”.` : `Aún no hay ${plural}.`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Nombre</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  {isAdmin ? <th className="px-4 py-3 text-right font-medium">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0">
                    {editingId === row.id ? (
                      <td colSpan={3} className="px-4 py-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                          <div className="flex-1">
                            <TextField
                              label="Nombre"
                              name={`edit-${row.id}`}
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              error={editError}
                              maxLength={80}
                              autoFocus
                            />
                          </div>
                          <div className="flex gap-2 sm:mt-7">
                            <Button size="sm" onClick={() => void saveEdit(row.id)} disabled={actingId === row.id}>
                              {actingId === row.id ? 'Guardando…' : 'Guardar'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium text-text-primary">{row.name}</td>
                        <td className="px-4 py-3">
                          <Badge tone={row.archived ? 'neutral' : row.active ? 'success' : 'warning'}>
                            {row.archived ? 'Archivada' : row.active ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        {isAdmin ? (
                          <td className="px-4 py-3 text-right">
                            <RowActionsMenu
                              actions={[
                                { label: 'Editar', onSelect: () => startEdit(row) },
                                ...(row.archived
                                  ? [{ label: 'Restaurar', onSelect: () => void update(row.id, { archived: false }) }]
                                  : [
                                      {
                                        label: row.active ? 'Desactivar' : 'Activar',
                                        onSelect: () => void toggle(row),
                                        disabled: actingId === row.id,
                                      },
                                      { label: 'Archivar', onSelect: () => setConfirm({ kind: 'archive', row }) },
                                    ]),
                                {
                                  label: 'Eliminar',
                                  tone: 'danger' as const,
                                  onSelect: () => setConfirm({ kind: 'delete', row }),
                                },
                              ]}
                            />
                          </td>
                        ) : null}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </Card>

      <ConfirmDialog
        open={confirm?.kind === 'archive'}
        title={`Archivar ${noun}`}
        confirmLabel="Archivar"
        onConfirm={() => update(confirm?.row.id ?? '', { archived: true })}
        onClose={() => setConfirm(null)}
      >
        «{confirm?.row.name}» deja de aparecer al crear productos, pero se conserva y los productos que ya la usan no
        cambian. Podrás restaurarla cuando quieras.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === 'delete'}
        title={`Eliminar ${noun}`}
        confirmLabel="Eliminar definitivamente"
        danger
        onConfirm={() => remove(confirm?.row.id ?? '')}
        onClose={() => setConfirm(null)}
      >
        «{confirm?.row.name}» se elimina para siempre. Solo es posible si ningún producto la usa, ni siquiera uno
        archivado; si alguno la usa, archívala en su lugar.
      </ConfirmDialog>
    </div>
  );
}

/**
 * Marcas y categorías (admin). One list at a time, chosen with tabs; the create button follows
 * the tab. Nothing is silently lost: an inactive or archived brand/category leaves the product
 * selectors, while the products already using it keep it.
 */
export function MarcasCategoriasPage() {
  const brands = useBrands();
  const categories = useCategories();
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteBrand = useDeleteBrand();
  const deleteCategory = useDeleteCategory();
  const isAdmin = useIsAdmin();

  const [tab, setTab] = useState<TabKey>('brands');
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState('recent');

  const brandRows: Row[] = (brands.data ?? []).map((b) => ({
    id: b.brandId,
    name: b.name,
    active: b.active,
    archived: b.archived,
    createdAt: b.createdAt,
  }));
  const categoryRows: Row[] = (categories.data ?? []).map((c) => ({
    id: c.categoryId,
    name: c.name,
    active: c.active,
    archived: c.archived,
    createdAt: c.createdAt,
  }));
  const countOf = (rows: Row[]) => rows.filter((r) => !r.archived).length;

  const selectTab = (next: TabKey) => {
    setTab(next);
    setCreating(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Marcas y categorías"
        description="Crea y administra las marcas y categorías que se eligen al crear un producto. Al desactivar o archivar una, los productos que ya la usan no cambian."
      />

      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border">
        <div role="tablist" aria-label="Marcas y categorías" className="flex gap-1">
          {(Object.keys(TABS) as TabKey[]).map((key) => {
            const selected = tab === key;
            const count = countOf(key === 'brands' ? brandRows : categoryRows);
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => selectTab(key)}
                className={cn(
                  '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected
                    ? 'border-brand-accent text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary',
                )}
              >
                {TABS[key].label}
                <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs text-text-muted">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-4">
          <SortSelect options={SORT_OPTIONS} value={sort} onChange={setSort} />
          <label className="inline-flex select-none items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-5 w-5 rounded border-border-strong text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            Mostrar archivadas
          </label>
          {isAdmin ? (
            <Button size="sm" onClick={() => setCreating(true)} disabled={creating}>
              {TABS[tab].newLabel}
            </Button>
          ) : null}
        </div>
      </div>

      {tab === 'brands' ? (
        <TabContent
          key="brands"
          tab="brands"
          rows={brandRows}
          isLoading={brands.isLoading}
          isError={brands.isError}
          error={brands.error}
          onRetry={() => void brands.refetch()}
          create={(name) => createBrand.mutateAsync({ name, active: true })}
          update={(id, input) => updateBrand.mutateAsync({ id, input })}
          remove={(id) => deleteBrand.mutateAsync(id)}
          showArchived={showArchived}
          sort={sort}
          creating={creating}
          onCloseCreate={() => setCreating(false)}
        />
      ) : (
        <TabContent
          key="categories"
          tab="categories"
          rows={categoryRows}
          isLoading={categories.isLoading}
          isError={categories.isError}
          error={categories.error}
          onRetry={() => void categories.refetch()}
          create={(name) => createCategory.mutateAsync({ name, active: true })}
          update={(id, input) => updateCategory.mutateAsync({ id, input })}
          remove={(id) => deleteCategory.mutateAsync(id)}
          showArchived={showArchived}
          sort={sort}
          creating={creating}
          onCloseCreate={() => setCreating(false)}
        />
      )}
    </div>
  );
}
